<template>
  <div :class="embedded ? 'journey-embedded' : 'journey-page'">
    <ContourHero v-if="!embedded" background :mask-selectors="['.journey-page .globe-wrap']" />
    <article class="journey-layout" :class="{'journey-preview': $route.query.preview === '1'}">
      <h1 v-if="!embedded" id="travels-title" class="journey-sr-title">Travels</h1>

      <div class="globe-wrap" ref="wrap" data-scroll-pop="true">
        <div
          class="globe-stage"
          ref="stage"
          @pointerenter="onGlobeEnter"
          @pointerleave="onGlobeLeave"
        >
          <canvas ref="canvas" class="globe-canvas" role="img" :aria-label="active ? `World globe, ${active.en} selected` : 'Interactive globe of places I’ve visited'"></canvas>

          <span
            v-if="ripple"
            :key="ripple.key"
            class="globe-ripple"
            :style="{ left: ripple.x + 'px', top: ripple.y + 'px' }"
            aria-hidden="true"
          ></span>

          <div
            class="globe-tooltip"
            :class="{ 'is-visible': !!active }"
            :style="tipStyle"
            :aria-hidden="String(!active)"
          >
            <div class="globe-tooltip-card" v-if="active">
              <button
                v-if="pinned"
                type="button"
                class="globe-tooltip-close"
                aria-label="Close selected place"
                @pointerdown.stop
                @click.stop="clearSelectedPlace"
              >
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
              </button>
              <img :data-full-src="active.img" :src="placePreview(active)" :alt="active.label" class="globe-tooltip-img" loading="lazy" decoding="async" @error="useOriginalImage($event, active)">
              <div class="globe-tooltip-text">
                <template v-if="active.local">
                  <span class="gt-local">{{ active.local }}</span>
                  <span class="gt-en">{{ active.label }}</span>
                </template>
                <!-- No separate local name (English-native): render it in the
                     local style (black, same font), not the secondary English style. -->
                <span v-else class="gt-local">{{ active.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <p v-if="embedded" class="globe-hint">Drag to spin · pinch to zoom · tap a point</p>
      </div>

      <p v-if="embedded" class="globe-places">
        <button
          v-for="(p, i) in places"
          :key="p.label"
          type="button"
          class="globe-chip"
          :class="{ 'is-active': active && active.en === p.en }"
          :aria-pressed="(!!active && active.en === p.en).toString()"
          @click="focusPlace(i)"
        >{{ p.label }}</button>
      </p>

      <TravelPhotoOrbit v-if="!embedded" />
    </article>
  </div>
</template>

<script>
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import { places, HOME } from '../data/places';
import ContourHero from './ContourHero.vue';
import TravelPhotoOrbit from './TravelPhotoOrbit.vue';

const placeHash = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

export default {
  name: 'JourneyTab',
  mixins: [scrollRevealMixin],
  components: { ContourHero, TravelPhotoOrbit },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    placesOverride: {
      type: Array,
      default: () => [],
    },
    initialFocus: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      places: this.placesOverride.length ? this.placesOverride : places,
      active: null,     // currently shown place (hover or selected)
      pinned: false,    // true when a place was tapped (sticks until tapping empty space)
      ripple: null,     // click ripple shown for a deep-linked place tap
      hoverIndex: null,
      pointerInsideGlobe: false,
      zoomLevel: 1,
      tipStyle: {},
      d3: null,
      land: null,
      graticule: null,
    };
  },
  async mounted() {
    this._journeyActive = true;
    // Load the globe libraries and world outline only on this route.
    const [{ geoOrthographic, geoPath, geoGraticule10, geoDistance }, topo] = await Promise.all([
      import('d3-geo'),
      import('topojson-client'),
    ]);
    const world = await fetch('/assets/data/land-110m.json').then(r => r.json());
    if (!this.$refs.canvas) return;

    this.d3 = { geoPath, geoDistance };
    this.land = topo.feature(world, world.objects.land);
    this.graticule = geoGraticule10();

    this.rotation = [-HOME[0], -22];
    this.velocity = 0.16;               // idle spin, degrees per frame-ish
    this.lastInteract = 0;
    this.dragging = false;
    this.pinching = false;
    this.tween = null;
    this.spinVelocity = [0, 0];
    this.screenPins = [];
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.projection = geoOrthographic().clipAngle(90).precision(0.4);
    this.ctx = this.$refs.canvas.getContext('2d');

    this.resize();
    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    if (!this.embedded) {
      // Gallery orientation can change the width cap after the window resize.
      this._wrapResize = new ResizeObserver(() => this.resize());
      this._wrapResize.observe(this.$refs.wrap);
    }

    const cv = this.$refs.canvas;
    cv.addEventListener('pointerdown', this.onDown);
    cv.addEventListener('wheel', this.onWheel, { passive: false });
    cv.addEventListener('touchstart', this.onTouchStart, { passive: false });
    cv.addEventListener('touchmove', this.onTouchMove, { passive: false });
    cv.addEventListener('touchend', this.onTouchEnd);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);

    this.startTime = performance.now();
    this.loop = (t) => {
      if (!this._journeyActive) {
        this._raf = null;
        return;
      }
      this.tick(t);
      this._raf = requestAnimationFrame(this.loop);
    };
    if (this._journeyActive) this._raf = requestAnimationFrame(this.loop);

    // Support a deep link like /travels#hong-kong from the home panel: open
    // that globe point the same way a tap would.
    const focusHash = this.initialFocus || (this.embedded ? '' : (this.$route.hash || '').replace(/^#/, ''));
    if (focusHash) {
      const idx = this.places.findIndex((p) => (
        placeHash(p.en) === placeHash(focusHash)
      ));
      if (idx >= 0) {
        if (this.embedded) this.deepLinkPlace(idx);
        else this.focusPlace(idx);
      }
    }
  },
  activated() {
    this._journeyActive = true;
    this.$nextTick(() => {
      if (this.projection) this.resize();
      if (this.loop && !this._raf) this._raf = requestAnimationFrame(this.loop);
    });
  },
  deactivated() {
    this._journeyActive = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    this.clearHoverState();
  },
  unmounted() {
    this._journeyActive = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    if (this._deepLinkScrollTimer) {
      window.clearTimeout(this._deepLinkScrollTimer);
      this._deepLinkScrollTimer = null;
    }
    if (this._deepLinkTimer) {
      window.clearTimeout(this._deepLinkTimer);
      this._deepLinkTimer = null;
    }
    if (this._rippleTimer) {
      window.clearTimeout(this._rippleTimer);
      this._rippleTimer = null;
    }
    this.clearHoverState();
    window.removeEventListener('resize', this._onResize);
    this._wrapResize?.disconnect();
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    const cv = this.$refs.canvas;
    if (cv) {
      cv.removeEventListener('pointerdown', this.onDown);
      cv.removeEventListener('wheel', this.onWheel);
      cv.removeEventListener('touchstart', this.onTouchStart);
      cv.removeEventListener('touchmove', this.onTouchMove);
      cv.removeEventListener('touchend', this.onTouchEnd);
    }
  },
  methods: {
    clearSelectedPlace() {
      this.pinned = false;
      this.clearHoverState();
      this.active = null;
      this.lastInteract = performance.now();
    },
    placePreview(place) {
      if (place.preview) return place.preview;
      const filename = String(place.img || '').split('/').pop();
      return filename ? `/assets/travel/previews/${filename}.webp${filename === 'travel-new-york.jpg' ? '?v=20260926' : ''}` : place.img;
    },
    useOriginalImage(event, place) {
      if (event.target.src !== place.img) event.target.src = place.img;
    },
    resize() {
      const wrap = this.$refs.wrap;
      if (!wrap) return;
      const size = Math.min(wrap.clientWidth, this.embedded ? 560 : 760);
      if (!size) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cv = this.$refs.canvas;
      this.size = size;
      cv.width = size * dpr;
      cv.height = size * dpr;
      cv.style.width = size + 'px';
      cv.style.height = size + 'px';
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.baseScale = size / 2 - 6;
      this.projection
        .translate([size / 2, size / 2]);
      this.applyZoom();
    },
    applyZoom() {
      if (!this.projection || !this.baseScale) return;
      this.projection.scale(this.baseScale * this.zoomLevel);
    },
    palette() {
      const dark = document.body.classList.contains('dark-mode');
      return dark
        ? { ocean: this.embedded ? 'rgba(255,255,255,0.02)' : '#1d1d1f', grat: 'rgba(255,255,255,0.08)',
            land: 'rgba(255,255,255,0.05)', coast: 'rgba(255,255,255,0.30)',
            outline: 'rgba(255,255,255,0.22)', pin: '#dcdcdc', active: '#ffffff',
            glow: '255,255,255' }
        : { ocean: this.embedded ? 'rgba(0,0,0,0.015)' : '#ffffff', grat: 'rgba(0,0,0,0.07)',
            land: 'rgba(0,0,0,0.05)', coast: 'rgba(0,0,0,0.24)',
            outline: 'rgba(0,0,0,0.18)', pin: '#444444', active: '#000000',
            glow: '40,40,40' };
    },
    tick(t) {
      const dt = Math.min((t - (this._last || t)) / 16.67, 3);
      this._last = t;
      const now = t;

      // tween the camera toward a tapped place
      if (this.tween) {
        const k = 0.14 * dt;
        this.rotation[0] += this.shortest(this.tween[0] - this.rotation[0]) * k;
        this.rotation[1] += (this.tween[1] - this.rotation[1]) * k;
        if (Math.abs(this.shortest(this.tween[0] - this.rotation[0])) < 0.3 &&
            Math.abs(this.tween[1] - this.rotation[1]) < 0.3) {
          this.rotation[0] = this.tween[0];
          this.rotation[1] = this.tween[1];
          this.tween = null;
        }
      } else if (!this.dragging && !this.pinching && !this.reduceMotion
        && (Math.abs(this.spinVelocity[0]) > 0.004 || Math.abs(this.spinVelocity[1]) > 0.004)) {
        this.rotation[0] += this.spinVelocity[0] * dt;
        const nextLatitude = this.rotation[1] + this.spinVelocity[1] * dt;
        this.rotation[1] = Math.max(-88, Math.min(88, nextLatitude));
        if (this.rotation[1] !== nextLatitude) this.spinVelocity[1] = 0;
        const friction = Math.pow(0.94, dt);
        this.spinVelocity[0] *= friction;
        this.spinVelocity[1] *= friction;
      } else if (!this.shouldPauseSpin(now)) {
        // idle auto-spin
        this.rotation[0] += this.velocity * dt;
      }

      this.projection.rotate(this.rotation);
      this.render();
    },
    render() {
      const ctx = this.ctx;
      const c = this.palette();
      const path = this.d3.geoPath(this.projection, ctx);
      const s = this.size;
      ctx.clearRect(0, 0, s, s);

      ctx.beginPath(); path({ type: 'Sphere' }); ctx.fillStyle = c.ocean; ctx.fill();
      ctx.beginPath(); path(this.graticule); ctx.lineWidth = 0.5; ctx.strokeStyle = c.grat; ctx.stroke();
      ctx.beginPath(); path(this.land); ctx.fillStyle = c.land; ctx.fill();
      ctx.lineWidth = 0.6; ctx.strokeStyle = c.coast; ctx.stroke();
      ctx.beginPath(); path({ type: 'Sphere' }); ctx.lineWidth = 1; ctx.strokeStyle = c.outline; ctx.stroke();

      // pins on the near hemisphere
      const center = [-this.rotation[0], -this.rotation[1]];
      this.screenPins = [];
      this.places.forEach((p, i) => {
        if (this.d3.geoDistance(p.coord, center) > Math.PI / 2) return;
        const xy = this.projection(p.coord);
        if (!xy) return;
        const [x, y] = xy;
        const isActive = this.active && this.active.en === p.en;
        this.screenPins.push({ i, x, y });

        // A quiet halo separates pins from coastlines without constant pulsing.
        const haloR = isActive ? 12 : 8;
        ctx.beginPath();
        ctx.arc(x, y, haloR, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${c.glow}, ${isActive ? 0.14 : 0.07})`;
        ctx.fill();

        // dot
        ctx.beginPath();
        ctx.arc(x, y, isActive ? 4.2 : 3, 0, 2 * Math.PI);
        ctx.fillStyle = isActive ? c.active : c.pin;
        ctx.fill();
        ctx.lineWidth = 1.4;
        ctx.strokeStyle = document.body.classList.contains('dark-mode') ? '#000000' : '#ffffff';
        ctx.stroke();
      });

      if (this.active) this.placeTooltip();
    },
    placeTooltip() {
      const hit = this.screenPins.find(sp => this.places[sp.i].en === this.active.en);
      if (!hit) { this.tipStyle = { opacity: 0 }; return; }
      this.tipStyle = {
        transform: `translate(${this.clampTooltipX(hit.x)}px, ${this.clampTooltipY(hit.y)}px)`,
      };
    },
    shouldPauseSpin(now) {
      return (
        this.dragging ||
        this.reduceMotion ||
        this.pointerInsideGlobe ||
        !!this.active ||
        (now - this.lastInteract) <= 2200
      );
    },
    clampTooltipX(x) {
      const width = 300;
      return Math.max(width / 2, Math.min(this.size - width / 2, x));
    },
    clampTooltipY(y) {
      return Math.max(172, y);
    },
    shortest(deg) {
      // normalize a longitude delta into [-180, 180] for the shortest spin
      let d = deg % 360;
      if (d > 180) d -= 360;
      if (d < -180) d += 360;
      return d;
    },
    hitTest(clientX, clientY) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = clientX - rect.left, y = clientY - rect.top;
      let best = null, bestD = 16; // px radius
      for (const sp of this.screenPins) {
        const d = Math.hypot(sp.x - x, sp.y - y);
        if (d < bestD) { bestD = d; best = sp; }
      }
      return best;
    },
    onGlobeEnter() {
      this.pointerInsideGlobe = true;
      this.lastInteract = performance.now();
    },
    onGlobeLeave() {
      this.pointerInsideGlobe = false;
      if (!this.pinned) {
        this.clearHoverState();
        this.active = null;
      }
      this.lastInteract = 0;
    },
    clearHoverState() {
      this.hoverIndex = null;
    },
    showHoverPlace(index) {
      this.active = this.places[index];
      this.hoverIndex = index;
    },
    onWheel(e) {
      // Only a trackpad pinch zooms. macOS/Windows deliver a pinch as a wheel
      // event with ctrlKey set; a plain two-finger scroll has ctrlKey false and
      // is left untouched so it scrolls the page.
      if (!e.ctrlKey) return;
      e.preventDefault();
      this.lastInteract = performance.now();
      this.spinVelocity = [0, 0];
      const factor = Math.exp(-e.deltaY * 0.01);
      this.zoomLevel = Math.max(0.8, Math.min(4.5, this.zoomLevel * factor));
      this.applyZoom();
    },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        this.pinching = true;
        this.dragging = false;
        this._pinchDist = this.touchDist(e.touches);
      }
    },
    onTouchMove(e) {
      if (this.pinching && e.touches.length === 2) {
        e.preventDefault();
        const d = this.touchDist(e.touches);
        if (this._pinchDist > 0) {
          const factor = d / this._pinchDist;
          this.zoomLevel = Math.max(0.8, Math.min(4.5, this.zoomLevel * factor));
          this.applyZoom();
        }
        this._pinchDist = d;
        this.lastInteract = performance.now();
      }
    },
    onTouchEnd(e) {
      if (!e.touches || e.touches.length < 2) this.pinching = false;
    },
    touchDist(t) {
      return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    },
    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragging = true;
      this.moved = 0;
      this.lastInteract = performance.now();
      this.tween = null;
      this.spinVelocity = [0, 0];
      this.clearHoverState();
      this._start = { x: e.clientX, y: e.clientY, rot: [...this.rotation] };
      this._lastPointer = { x: e.clientX, y: e.clientY, time: e.timeStamp };
      if (this.$refs.canvas.setPointerCapture) this.$refs.canvas.setPointerCapture(e.pointerId);
    },
    onMove(e) {
      if (this.pinching) return;
      if (this.dragging) {
        const dx = e.clientX - this._start.x;
        const dy = e.clientY - this._start.y;
        this.moved += Math.abs(dx) + Math.abs(dy);
        const k = 0.3; // degrees per pixel-ish, scaled by globe size
        this.rotation = [
          this._start.rot[0] + dx * k,
          Math.max(-88, Math.min(88, this._start.rot[1] - dy * k)),
        ];
        const elapsed = Math.max(8, e.timeStamp - this._lastPointer.time);
        const frameScale = 16.67 / elapsed;
        const nextVelocity = [
          (e.clientX - this._lastPointer.x) * k * frameScale,
          -(e.clientY - this._lastPointer.y) * k * frameScale,
        ];
        this.spinVelocity = [
          this.spinVelocity[0] * 0.55 + nextVelocity[0] * 0.45,
          this.spinVelocity[1] * 0.55 + nextVelocity[1] * 0.45,
        ];
        this._lastPointer = { x: e.clientX, y: e.clientY, time: e.timeStamp };
        this.lastInteract = performance.now();
        return;
      }
      // hover highlight (skip while a place is pinned by tap)
      if (this.pinned) return;
      const hit = this.hitTest(e.clientX, e.clientY);
      if (hit) {
        this.showHoverPlace(hit.i);
      } else {
        this.clearHoverState();
        this.active = null;
      }
    },
    onUp(e) {
      if (!this.dragging) return;
      this.dragging = false;
      this.lastInteract = performance.now();
      if (this.$refs.canvas.releasePointerCapture
        && this.$refs.canvas.hasPointerCapture
        && this.$refs.canvas.hasPointerCapture(e.pointerId)) {
        this.$refs.canvas.releasePointerCapture(e.pointerId);
      }
      if (this.moved < 6) {
        this.spinVelocity = [0, 0];
        // treat as a tap
        const hit = this.hitTest(e.clientX, e.clientY);
        if (hit) {
          this.focusPlace(hit.i);
        } else {
          this.pinned = false;
          this.clearHoverState();
          this.active = null;
        }
      }
    },
    focusPlace(i) {
      const p = this.places[i];
      if (!p || !this.projection) return;
      this.clearHoverState();
      this.active = p;
      this.pinned = true;
      this.lastInteract = performance.now();
      this.spinVelocity = [0, 0];
      this.tween = [-p.coord[0], -p.coord[1]];
    },
    // Deep link from the home panel: first bring the globe to the middle of
    // the viewport, then run the focus sequence (zoom in, then tap).
    deepLinkScrollToPlace(index) {
      const start = () => this.deepLinkPlace(index);
      const stage = this.$refs.stage;
      if (!stage || this.reduceMotion) {
        if (stage) stage.scrollIntoView({ behavior: 'auto', block: 'center' });
        start();
        return;
      }
      stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._deepLinkScrollTimer = window.setTimeout(start, 800);
    },
    // Settle the globe on the place while zooming in, then replay the tap
    // (ripple, then open) so the arrival reads as a real click rather than a
    // pre-opened state.
    deepLinkPlace(index) {
      const p = this.places[index];
      // Stop the idle spin so the arrival reads as a deliberate focus.
      this.velocity = 0;
      this.spinVelocity = [0, 0];
      this.tween = [-p.coord[0], -p.coord[1]];
      if (this.reduceMotion) {
        this.simulateTapAtPlace(index);
        return;
      }
      this.animateZoomTo(2.5, 1150);
      this._deepLinkTimer = window.setTimeout(() => {
        this.simulateTapAtPlace(index);
        this._deepLinkTimer = null;
      }, 1300);
    },
    animateZoomTo(target, duration) {
      const from = this.zoomLevel;
      const start = performance.now();
      const step = (t) => {
        if (!this.$refs.canvas) return;
        const k = Math.min(1, (t - start) / duration);
        const e = 1 - Math.pow(1 - k, 3);
        this.zoomLevel = from + (target - from) * e;
        this.applyZoom();
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    // Replay the tap that a user would make on the globe: ripple at the pin,
    // then open the place a moment later so the click reads as the cause.
    simulateTapAtPlace(index) {
      const findPin = () => {
        const pin = this.screenPins.find((sp) => sp.i === index);
        if (pin) {
          if (this.$refs.stage) {
            this.ripple = { x: pin.x, y: pin.y, key: Date.now() };
          }
          this._rippleTimer = window.setTimeout(() => {
            this.focusPlace(index);
            this._rippleTimer = window.setTimeout(() => {
              this.ripple = null;
              this._rippleTimer = null;
            }, 1000);
          }, 140);
        } else {
          requestAnimationFrame(findPin);
        }
      };
      findPin();
    },
  },
};
</script>

<style scoped>
:global(html:has(.journey-page)),
:global(body:has(.journey-page)) { overflow: hidden; }
.journey-page {
  --journey-ink: #222;
  --journey-quiet: #737373;
  --journey-surface: #f0f0f0;
  position: fixed;
  inset: clamp(80px, 12dvh, 116px) 0 0;
  width: 100vw;
  margin: 0;
  overflow: clip;
}
.journey-page .journey-layout {
  position: relative;
  width: 100%;
  min-height: 0;
  height: 100%;
  margin: 0;
  isolation: isolate;
}
.journey-sr-title { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.journey-page .globe-wrap {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(58vw, 780px, 78dvh);
  margin: 0;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;
}
.journey-page .globe-stage { pointer-events: auto; }
.journey-page .journey-layout:has(.travel-orbit:not(.is-vertical)) .globe-wrap { max-width: 58vw; }
.journey-page .globe-stage::before { content: none; }
.journey-page .globe-tooltip-img { pointer-events: auto; }
body.dark-mode .journey-page { --journey-ink: #eee; --journey-quiet: #aaa; --journey-surface: #303032; }
@media (max-width: 760px) {
  .journey-page { top: 64px; }
  .journey-page .globe-wrap { width: min(84vw, 560px, 72dvh); }
}
:global(html.preview-mode .journey-page) { inset: 0; }
.journey-page .journey-preview { min-height: 0; height: 100%; }
.journey-page .journey-preview .globe-wrap { width: min(68vw, 85svh); top: 50%; }
.journey-embedded,
.journey-embedded article {
  width: 100%;
  max-width: none;
  margin: 0;
}

.journey-embedded .globe-wrap {
  margin-top: 0;
}

.globe-wrap {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1.5rem 0 0.5rem;
}
.globe-stage {
  position: relative;
  line-height: 0;               /* shrink-wrap exactly to the canvas */
  overscroll-behavior: contain;
  border-radius: 50%;
  isolation: isolate;
}
.globe-stage::before {
  content: "";
  position: absolute;
  inset: 8%;
  z-index: -1;
  border-radius: 50%;
  background: radial-gradient(circle at 38% 32%, rgba(255, 255, 255, 0.72), rgba(210, 216, 224, 0.16) 56%, transparent 76%);
  pointer-events: none;
}
.globe-canvas {
  position: relative;
  z-index: 1;
  display: block;
  max-width: 100%;
  touch-action: none;           /* let us drag without the page scrolling */
  cursor: var(--site-grab-cursor, grab);
}
.globe-canvas:active {
  cursor: var(--site-grabbing-cursor, grabbing);
}
.globe-ripple {
  position: absolute;
  z-index: 2;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(110, 145, 175, 0.85);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: globe-ripple-a 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.globe-ripple::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 2px solid rgba(110, 145, 175, 0.55);
  border-radius: 50%;
  animation: globe-ripple-b 0.85s 0.18s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@keyframes globe-ripple-a {
  0% {
    transform: translate(-50%, -50%) scale(0.6);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(5);
    opacity: 0;
  }
}
@keyframes globe-ripple-b {
  0% {
    transform: scale(0.6);
    opacity: 1;
  }
  100% {
    transform: scale(5);
    opacity: 0;
  }
}
.globe-hint {
  margin-top: 0.6rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  color: #b3b3b3;
}
.globe-tooltip {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 180ms ease-out;
  z-index: 5;
}
.globe-tooltip.is-visible {
  opacity: 1;
}
.globe-tooltip.is-visible .globe-tooltip-close { pointer-events: auto; }
.globe-tooltip-card {
  position: absolute;
  left: 0;
  bottom: 18px;                  /* sit just above the pin */
  transform: translate(-50%, 0);
  width: 300px;
  max-width: 300px;
  background: rgba(252, 252, 253, 0.94);
  border: 1px solid rgba(60, 60, 67, 0.14);
  border-radius: 16px;
  box-shadow: none;
  backdrop-filter: blur(10px) saturate(120%);
  -webkit-backdrop-filter: blur(10px) saturate(120%);
  overflow: hidden;
  text-align: left;
}
.globe-tooltip-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(246, 246, 248, 0.88);
  color: #3a3a3c;
  cursor: pointer;
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(12px) saturate(150%);
  transition: background-color 160ms ease, transform 120ms cubic-bezier(0.22, 1, 0.36, 1);
  -webkit-tap-highlight-color: transparent;
}
.globe-tooltip-close span {
  position: absolute;
  width: 11px;
  height: 1px;
  border-radius: 1px;
  background: currentColor;
}
.globe-tooltip-close span:first-child { transform: rotate(45deg); }
.globe-tooltip-close span:last-child { transform: rotate(-45deg); }
.globe-tooltip-close:active { transform: scale(0.92); }
.globe-tooltip-close:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
.globe-tooltip-img {
  display: block;
  width: 100%;
  height: 188px;
  object-fit: cover;
  border-radius: 0;
  border-bottom: 1px solid rgba(60, 60, 67, 0.08);
}
.globe-tooltip-text {
  padding: 9px 12px 11px;
  display: flex;
  flex-direction: column;
}
.gt-local {
  font-family: "Noto Serif TC", "Noto Serif SC", "Source Han Serif TC", "Source Han Serif SC", "Songti TC", "STSong", serif;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #1f1f1f;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
.gt-en {
  font-family: "PT Serif", Georgia, serif;
  font-style: italic;
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  color: #777;
  margin-top: 2px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.globe-places {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 0.55rem;
  line-height: 1.35;
  margin: 0.75rem auto 0;
  max-width: 920px;
}
.globe-chip {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  color: #76767d;
  padding: 0.38rem 0.65rem;
  border: 1px solid rgba(60, 60, 67, 0.11);
  border-radius: 999px;
  background: rgba(248, 248, 250, 0.62);
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 160ms ease,
    background-color 160ms ease,
    border-color 160ms ease,
    transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
}
.globe-chip.is-active {
  color: #ffffff;
  border-color: #353539;
  background: #353539;
}
.globe-chip:active { transform: scale(0.96); }
.globe-chip:focus-visible {
  outline: 2px solid #4d4d52;
  outline-offset: 2px;
}
@media (hover: hover) and (pointer: fine) {
  .globe-chip:hover {
    color: #1f1f1f;
    background: rgba(255, 255, 255, 0.86);
  }
}

/* dark theme */
body.dark-mode .globe-tooltip-card {
  background: rgba(44, 44, 46, 0.94);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: none;
}
body.dark-mode .globe-tooltip-close {
  background: rgba(58, 58, 60, 0.9);
  color: #f5f5f7;
}
body.dark-mode .globe-stage::before {
  background: radial-gradient(circle at 38% 32%, rgba(255, 255, 255, 0.08), rgba(130, 145, 170, 0.045) 56%, transparent 76%);
}
body.dark-mode .gt-local { color: #ededed; }
body.dark-mode .gt-en { color: var(--site-text-tertiary); }
body.dark-mode .globe-hint { color: var(--site-text-tertiary); }
body.dark-mode .globe-chip {
  color: var(--site-text-secondary);
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(38, 38, 41, 0.62);
}
body.dark-mode .globe-chip.is-active {
  color: #1b1b1d;
  border-color: #ededed;
  background: #ededed;
}

:global(html.theme-transitioning .globe-tooltip-card),
:global(body.theme-transitioning .globe-tooltip-card) {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .gt-local),
:global(body.theme-transitioning .gt-local),
:global(html.theme-transitioning .gt-en),
:global(body.theme-transitioning .gt-en),
:global(html.theme-transitioning .globe-hint),
:global(body.theme-transitioning .globe-hint),
:global(html.theme-transitioning .globe-chip),
:global(body.theme-transitioning .globe-chip) {
  transition: color var(--theme-duration, 220ms) ease;
}

@media (max-width: 768px) {
  .globe-tooltip-card {
    width: min(280px, calc(100vw - 48px));
    max-width: min(280px, calc(100vw - 48px));
  }
  .globe-tooltip-img { height: 176px; }
  .globe-places {
    display: none;
  }
  .globe-places::-webkit-scrollbar { display: none; }
  .globe-chip { flex: 0 0 auto; scroll-snap-align: center; }
}
@media (prefers-reduced-motion: reduce) {
  .globe-tooltip,
  .globe-chip {
    transition: color 120ms ease, background-color 120ms ease, opacity 120ms ease;
  }
}
</style>
